import { useEffect, useState } from "react";
import { ChevronRight, Menu, X } from "lucide-react";
import { REGISTER_URL, navLinks } from "../data/content";
import { cn } from "../lib/utils";

export function Navbar(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={cn(
        "sticky top-0 z-40 w-full transition-all duration-300",
        scrolled
          ? "border-b border-surface-variant/40 bg-background/85 backdrop-blur-md shadow-bloom-sm"
          : "border-b border-transparent bg-transparent",
      )}
    >
      <div className="container-welope flex h-16 items-center justify-between gap-8 sm:h-20">
        <a href="/" aria-label="Welope — Startseite" className="group flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-bloom-sm transition-transform group-hover:rotate-[-6deg]">
            <PawLogo className="h-6 w-6 text-on-primary" />
          </span>
          <span className="font-headline text-headline-md leading-none text-primary">
            welope
          </span>
        </a>

        <nav aria-label="Hauptnavigation" className="hidden md:block">
          <ul className="flex items-center gap-8">
            {navLinks.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className="font-body text-label-md text-on-surface-variant transition-colors hover:text-primary"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a href={REGISTER_URL} className="btn-primary">
            Warteliste beitreten
          </a>
        </div>

        <button
          type="button"
          aria-label={open ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full text-primary transition-colors hover:bg-surface-container-high md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-surface-variant/40 bg-background md:hidden">
          <nav aria-label="Mobile Navigation" className="container-welope py-5">
            <ul className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between rounded-xl px-4 py-3 font-body text-body-md text-on-surface transition-colors hover:bg-surface-container-low"
                  >
                    {link.label}
                    <ChevronRight className="h-4 w-4 text-on-surface-variant" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-col gap-3 pt-4 border-t border-surface-variant/40">
              <a href={REGISTER_URL} className="btn-primary w-full" onClick={() => setOpen(false)}>
                Warteliste beitreten
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

/** Customs inline SVG pfoten-glyph — kein Emoji, konsistent mit Design-Regeln. */
function PawLogo({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      role="img"
    >
      <ellipse cx="12" cy="15" rx="4" ry="3" />
      <circle cx="7" cy="9" r="2" />
      <circle cx="17" cy="9" r="2" />
      <circle cx="4.5" cy="13" r="1.6" />
      <circle cx="19.5" cy="13" r="1.6" />
    </svg>
  );
}
